import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { fork } from "@formant/core/common/fork";
import { throttle } from "@formant/core/common/throttle";
import { IGroup } from "@formant/core/model/IGroup";
import { IStripeInvoice } from "@formant/core/model/IStripeInvoice";
import { ITagTemplate } from "@formant/core/model/ITagTemplate";
import { HStack } from "@formant/ui/component/common/HStack";
import { Icon } from "@formant/ui/component/common/Icon";
import { TextField } from "@formant/ui/component/common/TextField";

import {
    appStore,
    groupStore,
    organizationStore,
    tagTemplateStore,
} from "../../../stores";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { Section } from "../../common/Section";
import { SelectField } from "../../common/SelectField";
import { Switch } from "../../common/Switch";
import { DataUsage } from "../../Internal/DataUsage";
import { SettingsPage } from "../SettingsPage";
import * as styles from "./index.scss";
import { OrganizationPromptBuilder } from "./OrganizationPromptBuilder";
import { RestrictedBillingUsageLabel } from "./RestrictedBillingUsageLabel";

interface ITagsAndGroups {
    tag: ITagTemplate;
    groups: IGroup[];
}

@observer
export class Organization extends Component<
    any,
    { groupTagTemplates: ITagsAndGroups[] }
> {
    @observable
    selectedInvoice?: IStripeInvoice;

    @observable
    description?: string =
        (organizationStore.authenticated() || undefined)?.description || "";
    @observable
    defaultGroupCategory?: string =
        (organizationStore.authenticated() || undefined)
            ?.defaultGroupCategory || "";

    constructor(props: any) {
        super(props);
        this.state = {
            groupTagTemplates: [],
        };
    }

    componentDidMount(): void {
        void this.fetchTagTemplates();
        const org = organizationStore.authenticated();
        if (!org) {
            return;
        }
        this.description = org.description || "";
        this.defaultGroupCategory = org.defaultGroupCategory || "";
    }

    fetchTagTemplates = async () => {
        const [templates, groups] = await Promise.all([
            tagTemplateStore.listAsync(),
            groupStore.fetchList(),
        ]);

        const data: ITagsAndGroups[] = templates
            .map(template => ({
                tag: template,
                groups: groups.filter(
                    _ => _.tagKey === template.tagKey && _.active,
                ),
            }))
            .filter(_ => _.tag.isGroup && _.groups.length > 0);
        this.setState({ groupTagTemplates: data });
    };

    onSaveDescription = throttle(async (description: string) => {
        this.description = description;
        const org = organizationStore.authenticated();
        if (!org || !org.id) {
            return;
        }
        await organizationStore.update(org.id, {
            description: description.trim(),
        });
        return;
    }, 2000);

    onChangeDefaultGroupCategory = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const tagKey = event.target.value;
        this.defaultGroupCategory = tagKey;
        const org = organizationStore.authenticated();
        if (!org || !org.id) {
            return;
        }
        await organizationStore.update(org.id, {
            defaultGroupCategory: tagKey || null,
        });
        return;
    };

    public render() {
        const { selectedInvoice } = this;
        const organization = organizationStore.authenticated() || undefined;
        const { skipGetStarted } = appStore;
        const { groupTagTemplates } = this.state;
        if (!organization || !organization.id) {
            return false;
        }

        const invoices = organization.stripeInfo
            ? organizationStore.getStripeInvoices(organization.id) || []
            : undefined;

        return (
            <SettingsPage
                narrow={true}
                scrollable={true}
                className={styles.organization}
                title="Organization"
            >
                <HStack vAlign="center" className={styles.info}>
                    <HStack vAlign="center" className={styles.name}>
                        <Icon name="organization" className={styles.icon} />
                        <span>{organization.name}</span>
                    </HStack>
                    <RestrictedBillingUsageLabel
                        organizationId={organization.id}
                    />
                </HStack>

                <RestrictedComponent
                    resource="billing"
                    requiredAccess="administrator"
                    requiredFeature="billing"
                >
                    <DataUsage
                        organization={organization}
                        enableCost={false}
                        invoices={invoices}
                        selectedInvoice={selectedInvoice}
                        onSelectInvoice={this.onSelectInvoice}
                    />
                </RestrictedComponent>

                <Section title="Other settings">
                    <SelectField
                        label="Default group category"
                        value={this.defaultGroupCategory || undefined}
                        onChange={this.onChangeDefaultGroupCategory}
                        className={styles.field}
                    >
                        <option value="">None</option>
                        {groupTagTemplates?.map(_ => (
                            <option key={_.tag.tagKey} value={_.tag.tagKey}>
                                {_.tag.tagKey}
                            </option>
                        ))}
                    </SelectField>

                    {organizationStore.explorationsEnabled() ? (
                        <OrganizationPromptBuilder
                            text={this.description}
                            onTextChange={(newDescription: string) => {
                                this.description = newDescription;
                                fork(
                                    (async () => {
                                        await this.onSaveDescription(
                                            newDescription,
                                        );
                                    })(),
                                );
                            }}
                        />
                    ) : (
                        <TextField
                            label="Description"
                            value={this.description}
                            onChange={change => {
                                this.description = change.target.value;
                                fork(
                                    (async () => {
                                        await this.onSaveDescription(
                                            change.target.value,
                                        );
                                    })(),
                                );
                            }}
                        />
                    )}
                    <Switch
                        label="Show onboarding wizard"
                        info="Show onboarding wizard if no devices have been created (setting is stored locally)"
                        state={skipGetStarted}
                        onChange={this.onToggleSkipGetStarted}
                    />
                </Section>
            </SettingsPage>
        );
    }

    public onSelectInvoice = (selectedInvoice: IStripeInvoice) => {
        this.selectedInvoice = selectedInvoice;
    };

    public onToggleSkipGetStarted = (newState: boolean) => {
        appStore.skipGetStarted = newState;
    };
}
